import React, { useEffect, useState, useRef, useCallback } from "react";
import { Navigate, Outlet, useLocation  } from "react-router-dom";


const SkeletonLoader = () => {
  const [isDarkMode] = useState(true);

  const NavSkeleton = () => (
    <nav className="fixed top-0 left-0 w-full z-50 bg-gray-900/80 backdrop-blur-md">
      <div className="container mx-auto flex justify-between items-center p-4">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gray-700 rounded-full animate-pulse" />
          <div className="w-32 h-6 bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="flex space-x-4">
          {[1, 2, 3].map((item) => (
            <div 
              key={item} 
              className="w-20 h-8 bg-gray-700 rounded-full animate-pulse" 
            />
          ))}
        </div>
      </div>
    </nav>
  );

  const HeroSkeleton = () => (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800 opacity-70" />
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <div className="w-3/4 mx-auto h-16 bg-gray-700 rounded-lg animate-pulse" />
            <div className="w-full h-8 bg-gray-700 rounded-lg animate-pulse" />
            <div className="w-5/6 mx-auto h-6 bg-gray-700 rounded-lg animate-pulse" />
            
            <div className="flex justify-center space-x-4 pt-8">
              {[1, 2].map((btn) => (
                <div 
                  key={btn}
                  className="w-40 h-12 bg-gray-700 rounded-full animate-pulse" 
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const FeaturesSkeleton = () => (
    <section className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="w-1/2 mx-auto h-12 bg-gray-700 rounded-lg animate-pulse mb-6" />
          <div className="w-3/4 mx-auto h-6 bg-gray-700 rounded-lg animate-pulse" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3].map((feature) => (
            <div 
              key={feature}
              className="bg-gray-800 p-6 rounded-2xl space-y-4"
            >
              <div className="w-16 h-16 bg-gray-700 rounded-xl animate-pulse" />
              <div className="w-3/4 h-6 bg-gray-700 rounded-lg animate-pulse" />
              <div className="w-full h-4 bg-gray-700 rounded-lg animate-pulse" />
              <div className="w-1/2 h-4 bg-gray-700 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const StatisticsSkeleton = () => (
    <section className="py-20 bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
          {[1, 2, 3, 4].map((stat) => (
            <div 
              key={stat}
              className="bg-gray-900 p-6 rounded-2xl space-y-4"
            >
              <div className="w-16 h-16 mx-auto bg-gray-700 rounded-full animate-pulse" />
              <div className="w-1/2 mx-auto h-8 bg-gray-700 rounded-lg animate-pulse" />
              <div className="w-3/4 mx-auto h-4 bg-gray-700 rounded-lg animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const TestimonialsSkeleton = () => (
    <section className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="w-1/2 mx-auto h-12 bg-gray-700 rounded-lg animate-pulse mb-6" />
          <div className="w-3/4 mx-auto h-6 bg-gray-700 rounded-lg animate-pulse" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[1, 2, 3].map((testimonial) => (
            <div 
              key={testimonial}
              className="bg-gray-800 p-6 rounded-2xl space-y-4"
            >
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-700 rounded-full animate-pulse" />
                <div className="flex-grow space-y-2">
                  <div className="w-3/4 h-4 bg-gray-700 rounded-lg animate-pulse" />
                  <div className="w-1/2 h-4 bg-gray-700 rounded-lg animate-pulse" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="w-full h-4 bg-gray-700 rounded-lg animate-pulse" />
                <div className="w-full h-4 bg-gray-700 rounded-lg animate-pulse" />
                <div className="w-3/4 h-4 bg-gray-700 rounded-lg animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const FooterSkeleton = () => (
    <footer className="bg-gray-800 py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((col) => (
            <div key={col} className="space-y-4">
              <div className="w-1/2 h-8 bg-gray-700 rounded-lg animate-pulse" />
              <div className="space-y-2">
                {[1, 2, 3].map((link) => (
                  <div 
                    key={link}
                    className="w-3/4 h-4 bg-gray-700 rounded-lg animate-pulse" 
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="w-1/2 mx-auto h-4 bg-gray-700 rounded-lg animate-pulse" />
        </div>
      </div>
    </footer>
  );

  return (
    <div className="bg-gray-900 text-white">
      <NavSkeleton />
      <HeroSkeleton />
      <FeaturesSkeleton />
      <StatisticsSkeleton />
      <TestimonialsSkeleton />
      <FooterSkeleton />
    </div>
  );
};
// Enhanced Authentication Service
const AuthService = {
  async verifySession() {
    try {
      const response = await fetch('/auth/verify-session', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();

      return {
        authenticated: true,
        tools: data.tools || []  // Store allowed tool names
      };
    } catch (error) {
      return {
        authenticated: false,
        tools: []
      };
    }
  }
};


const ProtectedRoute = () => {
  const [authState, setAuthState] = useState({
    isAuthenticated: null,
    isLoading: true,
    tools: []  // Store allowed tools from backend
  });

  const isRequestInProgress = useRef(false);
  const location = useLocation();  // Get current route

  const checkAuthentication = useCallback(async () => {
    if (isRequestInProgress.current) return;

    isRequestInProgress.current = true;

    try {
      const result = await AuthService.verifySession();
      setAuthState({
        isAuthenticated: result.authenticated,
        isLoading: false,
        tools: result.tools
      });
    } catch (error) {
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        tools: []
      });
    } finally {
      isRequestInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    checkAuthentication();
    const intervalId = setInterval(checkAuthentication, 5 * 60 * 1000); // Check every 5 mins
    return () => clearInterval(intervalId);
  }, [checkAuthentication]);

  // Show loading state
  if (authState.isLoading) {
    return <SkeletonLoader />;
  }

  // Check if user is authenticated
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Extract the base route (e.g., "/dashboard" from "/dashboard/settings")
  const currentPath = location.pathname.split('/')[1];

  // Ensure user has access to the current route
  if (!authState.tools.includes(currentPath)) {
    return <Navigate to="/404" />;  // Redirect if not allowed
  }

  return <Outlet />;
};


export default ProtectedRoute;